'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import Label from '@/components/typography/label';
import P from '@/components/typography/p';
import { Button, buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getLocalizedPath } from '@/lib/utils/navigation';
import { Locales } from 'i18nConfig';
import Link from 'next/link';
import { useParams } from 'next/navigation';

const emailSchema = z.object({
    email: z
        .string()
        .min(1, 'contactCard.required')
        .email('contactCard.wrongEmail'),
    termsAccepted: z.literal(true, {
        errorMap: () => ({ message: 'contactCard.termsError' }),
    }),
});

type EmailStepFormValues = z.infer<typeof emailSchema>;

interface SubscriptionInputProps {
    className?: string;
    footer?: boolean;
}

export default function SubscriptionInput({
    className,
    footer,
}: SubscriptionInputProps) {
    const { t } = useTranslation();
    const { locale }: { locale: Locales } = useParams();

    const [step, setStep] = useState<'email' | 'name'>('email');
    const [name, setName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const {
        register,
        control,
        handleSubmit,
        trigger,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<EmailStepFormValues>({
        resolver: zodResolver(emailSchema),
        defaultValues: {
            email: '',
        },
        mode: 'onSubmit',
    });

    const handleNext = async () => {
        const isValid = await trigger('email');
        if (!isValid) return;

        setStep('name');
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setName(value);
        setErrorMessage('');
        setSuccessMessage('');

        if (value.includes('@')) {
            setErrorMessage(
                t('contactCard.nameCannotBeEmail') ||
                    'Voer je naam in, niet je e-mailadres.',
            );
        }
    };

    const onSubmit = async (data: EmailStepFormValues) => {
        setErrorMessage('');
        setSuccessMessage('');

        if (!name.trim()) {
            setErrorMessage(t('contactCard.required'));
            return;
        }

        try {
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email: data.email }),
            });

            if (!response.ok) throw new Error('Request failed');

            setSuccessMessage(t('contactCard.success'));
            reset();
            setName('');
            setStep('email');
        } catch {
            setErrorMessage(t('contactCard.error'));
        }
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex w-full flex-col"
        >
            <div className={`flex w-full ${className}`}>
                <div
                    className={`${step === 'email' ? '' : 'hidden'} flex w-full`}
                >
                    <Input
                        variant="outline"
                        className="w-full"
                        placeholder={t('contactCard.email')}
                        {...register('email')}
                    />
                    <div
                        onClick={handleNext}
                        className={cn(buttonVariants({ variant: 'send' }), {
                            'bg-yellow! hover:text-yellow! text-black hover:bg-black!':
                                footer,
                        })}
                    >
                        <Label
                            className="underline"
                            color="inherit"
                            variant="button"
                        >
                            {t('contactCard.next')}
                        </Label>
                    </div>
                </div>

                <div
                    className={`${step === 'name' ? '' : 'hidden'} flex w-full`}
                >
                    <Input
                        variant="outline"
                        className="w-full"
                        wrapperClassName="mr-1"
                        placeholder={t('contactCard.name')}
                        value={name}
                        onChange={handleNameChange}
                    />
                    <Button
                        type="submit"
                        disabled={
                            isSubmitting ||
                            name.trim() === '' ||
                            name.includes('@')
                        }
                        variant={'send'}
                        className={
                            footer
                                ? 'bg-yellow! hover:text-yellow! text-black! hover:bg-black!'
                                : ''
                        }
                    >
                        <Label
                            className="abc-italic underline"
                            color="inherit"
                            variant="button"
                        >
                            {isSubmitting
                                ? t('contactCard.submitting')
                                : t('contactCard.send')}
                        </Label>
                    </Button>
                </div>
            </div>

            {step === 'name' && (
                <div className="mt-4 flex items-center space-x-2">
                    <Controller
                        name="termsAccepted"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                id="terms"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                        )}
                    />
                    <label htmlFor="terms" className="text-sm">
                        {t('contactCard.termsText')}{' '}
                        <Link
                            href={getLocalizedPath('terms', locale)}
                            className="w-fit underline"
                        >
                            {t('contactCard.termsLinkText')}
                        </Link>
                        *
                    </label>
                </div>
            )}

            {errorMessage && (
                <P className="mt-2 text-xs text-red-600 lg:mt-5">
                    {errorMessage}
                </P>
            )}

            {errors.email && step === 'email' && (
                <P className="mt-2 text-xs text-red-600 lg:mt-5">
                    {t(errors.email.message as keyof typeof errors.email)}
                </P>
            )}

            {errors.termsAccepted && step === 'name' && (
                <P className="mt-2 text-xs text-red-600 lg:mt-5">
                    {t(
                        errors.termsAccepted
                            .message as keyof typeof errors.termsAccepted,
                    )}
                </P>
            )}

            {successMessage && (
                <P className="mt-2 text-xs text-green-600 lg:mt-5">
                    {successMessage}
                </P>
            )}
        </form>
    );
}
